import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendCode, forgotPassword } from '@/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [toast, setToast] = useState('');
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendCode = async () => {
    if (!phone.trim()) {
      setToast('请输入手机号');
      setTimeout(() => setToast(''), 2000);
      return;
    }
    if (phone.trim().length !== 11) {
      setToast('请输入正确的手机号');
      setTimeout(() => setToast(''), 2000);
      return;
    }
    if (sending || countdown > 0) return;
    setSending(true);
    try {
      const res = await sendCode(phone.trim());
      setCountdown(60);
      //短信通道未接入前接口直接返回验证码
      setToast(res.code ? `验证码已发送：${res.code}` : '验证码已发送');
      setTimeout(() => setToast(''), 3000);
    } catch (err) {
      setToast(err instanceof Error ? err.message : '发送失败');
      setTimeout(() => setToast(''), 2000);
    } finally {
      setSending(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || !code.trim() || !password.trim() || !confirmPassword.trim()) {
      setToast('请填写所有必填项');
      setTimeout(() => setToast(''), 2000);
      return;
    }
    if (password !== confirmPassword) {
      setToast('两次输入的密码不一致');
      setTimeout(() => setToast(''), 2000);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      await forgotPassword(phone.trim(), code.trim(), password, confirmPassword);
      setToast('密码重置成功');
      setTimeout(() => {
        setToast('');
        navigate('/login');
      }, 1200);
    } catch (err) {
      setToast(err instanceof Error ? err.message : '重置失败');
      setTimeout(() => setToast(''), 2000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mobile-container bg-white min-h-screen flex flex-col">
      {/* Top bar: back arrow */}
      <div className="flex items-center px-4 h-12">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center cursor-pointer active:scale-95 transition-transform"
          aria-label="返回"
        >
          <i className="ri-arrow-left-s-line text-foreground-800 text-xl" />
        </button>
      </div>

      {/* Logo area */}
      <div className="flex flex-col items-center mt-2 mb-8">
        <div className="relative">
          <div className="absolute inset-0 bg-primary-400/25 rounded-3xl blur-xl scale-125" />
          <div className="relative w-16 h-16 rounded-2xl bg-primary-500 flex items-center justify-center">
            <span className="text-white text-2xl font-bold tracking-wider">众</span>
          </div>
        </div>
        <h1 className="mt-4 text-foreground-950 text-xl font-bold tracking-wide">众泰成</h1>
        <p className="mt-1 text-foreground-400 text-sm tracking-wider">重置登录密码</p>
      </div>

      {/* Reset form */}
      <form onSubmit={handleReset} className="px-6 flex-1">
        <h2 className="text-foreground-950 text-lg font-bold mb-6">忘记密码</h2>

        {/* Phone input */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-smartphone-line text-lg" />
          </div>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="请输入手机号"
            maxLength={11}
            className="w-full h-12 pl-12 pr-4 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
          />
        </div>

        {/* Verification code input */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-message-2-line text-lg" />
          </div>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="请输入验证码"
            maxLength={6}
            className="w-full h-12 pl-12 pr-28 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
          />
          <button
            type="button"
            onClick={handleSendCode}
            disabled={countdown > 0}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-8 px-3 rounded-lg text-xs font-bold whitespace-nowrap cursor-pointer transition-colors disabled:cursor-not-allowed text-primary-500 bg-primary-100/70 hover:bg-primary-100 disabled:text-foreground-400 disabled:bg-background-100"
          >
            {countdown > 0 ? `${countdown}s` : '获取验证码'}
          </button>
        </div>

        {/* New password input */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-shield-keyhole-line text-lg" />
          </div>
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="请输入新密码"
            className="w-full h-12 pl-12 pr-12 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400 cursor-pointer hover:text-foreground-600 transition-colors"
            aria-label={showPassword ? '隐藏密码' : '显示密码'}
          >
            <i className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`} />
          </button>
        </div>

        {/* Confirm new password input */}
        <div className="relative mb-6">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-shield-check-line text-lg" />
          </div>
          <input
            type={showConfirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="请确认新密码"
            className="w-full h-12 pl-12 pr-12 rounded-xl bg-white border border-background-300 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400 cursor-pointer hover:text-foreground-600 transition-colors"
            aria-label={showConfirm ? '隐藏密码' : '显示密码'}
          >
            <i className={`${showConfirm ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`} />
          </button>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          className="w-full h-12 rounded-xl bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-2 cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all shadow-lg shadow-primary-500/20"
        >
          重置密码
          <i className="ri-arrow-right-line text-sm" />
        </button>

        {/* Back to login link */}
        <div className="flex items-center justify-center mt-8 gap-2">
          <span className="text-foreground-400 text-sm">想起密码了?</span>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-primary-500 text-sm font-bold cursor-pointer hover:text-primary-600 transition-colors"
          >
            返回登录
          </button>
        </div>
      </form>

      {/* Bottom hint */}
      <div className="px-6 pb-8 pt-4 mt-auto">
        <p className="text-center text-foreground-400 text-xs leading-relaxed">
          如有疑问，请联系平台客服协助处理
        </p>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-sm rounded-2xl text-white text-sm z-50 shadow-xl">
          {toast}
        </div>
      )}
    </div>
  );
}